import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Grid,
  InputAdornment,
  Paper,
  Divider,
} from "@mui/material";
import { HexColorPicker } from "react-colorful";
import NewColors from "../../../../themes/NewColors";

// Helper to validate hex colors
const isValidHex = (color) => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

// Get brightness to determine text color
const getBrightness = (hexColor) => {
  if (!isValidHex(hexColor)) return 128;

  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculate brightness using a common formula
  return (r * 299 + g * 587 + b * 114) / 1000;
};

// Get appropriate text color for a background color
const getTextColor = (backgroundColor) => {
  return getBrightness(backgroundColor) > 140 ? "#000000" : "#ffffff";
};

const AdvancedBrandingEditorDialog = ({
  open,
  onClose,
  initialColors,
  onSave,
}) => {
  const [colors, setColors] = useState({
    primary: "#5e35b1",
    primaryLight: "#9162e4",
    secondary: "#ff6f00",
    secondaryLight: "#ffad42",
    primaryText: "#ffffff",
    primaryLightText: "#ffffff",
    secondaryText: "#ffffff",
    secondaryLightText: "#ffffff",
  });

  const [activeTab, setActiveTab] = useState(0);
  const [currentEditingColor, setCurrentEditingColor] = useState("primary");
  const [colorErrors, setColorErrors] = useState({});

  // Initialize with provided colors when dialog opens
  useEffect(() => {
    if (open && initialColors) {
      // Use default colors for any missing values
      const baseColors = {
        primary: initialColors.primary || "#5e35b1",
        primaryLight: initialColors.primaryLight || "#9162e4",
        secondary: initialColors.secondary || "#ff6f00",
        secondaryLight: initialColors.secondaryLight || "#ffad42",
      };

      // Add text colors or calculate them if not provided
      const updatedColors = {
        ...baseColors,
        primaryText:
          initialColors.primaryText || getTextColor(baseColors.primary),
        primaryLightText:
          initialColors.primaryLightText ||
          getTextColor(baseColors.primaryLight),
        secondaryText:
          initialColors.secondaryText || getTextColor(baseColors.secondary),
        secondaryLightText:
          initialColors.secondaryLightText ||
          getTextColor(baseColors.secondaryLight),
      };

      setColors(updatedColors);
    }
  }, [open, initialColors]);

  const handleColorChange = (colorKey, value) => {
    const updatedColors = { ...colors, [colorKey]: value };

    // If changing a background color, update the corresponding text color
    if (
      colorKey === "primary" ||
      colorKey === "primaryLight" ||
      colorKey === "secondary" ||
      colorKey === "secondaryLight"
    ) {
      const textKey = `${colorKey}Text`;
      updatedColors[textKey] = getTextColor(value);
    }

    // Update the colors
    setColors(updatedColors);

    // Clear error if it's now valid
    if (isValidHex(value)) {
      setColorErrors({ ...colorErrors, [colorKey]: null });
    } else {
      setColorErrors({ ...colorErrors, [colorKey]: "Invalid hex color" });
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePickerColorChange = (newColor) => {
    handleColorChange(currentEditingColor, newColor);
  };

  const handleSave = () => {
    // Check if all colors are valid
    const hasErrors = Object.entries(colors).some(([key, value]) => {
      // Only validate hex colors, not text color properties
      if (key.includes("Text")) return false;
      return !isValidHex(value);
    });

    if (hasErrors) {
      // Update all error states
      const newErrors = {};
      Object.entries(colors).forEach(([key, value]) => {
        if (!key.includes("Text") && !isValidHex(value)) {
          newErrors[key] = "Invalid hex color";
        }
      });
      setColorErrors(newErrors);
      return;
    }

    // Call save handler with new colors
    onSave(colors);
    onClose();
  };

  // Auto-suggest function from our previous implementation
  const suggestColors = () => {
    const primary = colors.primary;

    if (!primary || !isValidHex(primary)) {
      setColorErrors({ ...colorErrors, primary: "Invalid primary color" });
      return;
    }

    // Convert hex to RGB
    const hexToRgb = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    };

    // Convert RGB to hex
    const rgbToHex = ({ r, g, b }) => {
      return `#${Math.round(r).toString(16).padStart(2, "0")}${Math.round(g).toString(16).padStart(2, "0")}${Math.round(b).toString(16).padStart(2, "0")}`;
    };

    // Calculate luminance for contrast calculation
    const getLuminance = ({ r, g, b }) => {
      const rsRGB = r / 255;
      const gsRGB = g / 255;
      const bsRGB = b / 255;

      const R =
        rsRGB <= 0.03928
          ? rsRGB / 12.92
          : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
      const G =
        gsRGB <= 0.03928
          ? gsRGB / 12.92
          : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
      const B =
        bsRGB <= 0.03928
          ? bsRGB / 12.92
          : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

      return 0.2126 * R + 0.7152 * G + 0.0722 * B;
    };

    // Calculate contrast ratio between colors
    const getContrastRatio = (rgb1, rgb2) => {
      const lum1 = getLuminance(rgb1);
      const lum2 = getLuminance(rgb2);
      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);
      return (brightest + 0.05) / (darkest + 0.05);
    };

    // Check if color is too light for white text
    const isTooLightForWhiteText = (rgb) => {
      const WHITE = { r: 255, g: 255, b: 255 };
      const MIN_CONTRAST = 4.5; // WCAG AA standard for normal text

      return getContrastRatio(rgb, WHITE) < MIN_CONTRAST;
    };

    // Darken a color to ensure good contrast with white text
    const ensureContrastWithWhiteText = (rgb) => {
      const WHITE = { r: 255, g: 255, b: 255 };
      const MIN_CONTRAST = 4.5; // WCAG AA standard for normal text

      const adjustedRgb = { ...rgb };

      // If contrast with white is too low, darken the color
      while (getContrastRatio(adjustedRgb, WHITE) < MIN_CONTRAST) {
        // Darken by reducing RGB values
        adjustedRgb.r = Math.max(0, adjustedRgb.r * 0.9);
        adjustedRgb.g = Math.max(0, adjustedRgb.g * 0.9);
        adjustedRgb.b = Math.max(0, adjustedRgb.b * 0.9);

        // Safety exit if we're getting too dark
        if (adjustedRgb.r < 20 && adjustedRgb.g < 20 && adjustedRgb.b < 20) {
          // Return a medium-dark value that should have good contrast with white
          return { r: 50, g: 50, b: 50 };
        }
      }

      return adjustedRgb;
    };

    // Create a shade of a color (darker or lighter)
    const createShade = (rgb, factor, ensureWhiteTextContrast = false) => {
      // Factor > 1 lightens, factor < 1 darkens
      const result = {
        r: Math.min(255, Math.max(0, rgb.r * factor)),
        g: Math.min(255, Math.max(0, rgb.g * factor)),
        b: Math.min(255, Math.max(0, rgb.b * factor)),
      };

      // If we need to ensure contrast with white text and the color is too light
      if (ensureWhiteTextContrast && isTooLightForWhiteText(result)) {
        return ensureContrastWithWhiteText(result);
      }

      return result;
    };

    // Adjust color saturation
    const adjustSaturation = (rgb, factor) => {
      // Convert RGB to HSL
      const r = rgb.r / 255;
      const g = rgb.g / 255;
      const b = rgb.b / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h,
        s,
        // eslint-disable-next-line prefer-const
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0; // achromatic
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
          default:
            h = 0;
        }

        h /= 6;
      }

      // Adjust saturation
      s = Math.min(1, Math.max(0, s * factor));

      // Convert back to RGB
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      const hueToRgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      return {
        r: Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
        g: Math.round(hueToRgb(p, q, h) * 255),
        b: Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
      };
    };

    // Start with the primary color
    const primaryRgb = hexToRgb(primary);

    // Create a slightly more saturated primary color
    const adjustedPrimaryRgb = adjustSaturation(primaryRgb, 1.2);
    const adjustedPrimary = rgbToHex(adjustedPrimaryRgb);

    // Create lighter version for primaryLight, ensure it works with white text
    const primaryLightRgb = createShade(adjustedPrimaryRgb, 1.3, true);
    const primaryLight = rgbToHex(primaryLightRgb);

    // Create complementary color for secondary, ensure it works with white text
    const hueShiftedSecondaryRgb = adjustSaturation(
      {
        r: 255 - adjustedPrimaryRgb.r,
        g: 255 - adjustedPrimaryRgb.g,
        b: 255 - adjustedPrimaryRgb.b,
      },
      1.2,
    );

    // Ensure secondary has good contrast with white text
    const secondaryRgb = isTooLightForWhiteText(hueShiftedSecondaryRgb)
      ? ensureContrastWithWhiteText(hueShiftedSecondaryRgb)
      : hueShiftedSecondaryRgb;

    const secondary = rgbToHex(secondaryRgb);

    // Create lighter version of secondary, ensuring it works with white text
    const secondaryLightRgb = createShade(secondaryRgb, 1.3, true);
    const secondaryLight = rgbToHex(secondaryLightRgb);

    // Determine text colors based on background brightness
    const primaryText = getTextColor(adjustedPrimary);
    const primaryLightText = getTextColor(primaryLight);
    const secondaryText = getTextColor(secondary);
    const secondaryLightText = getTextColor(secondaryLight);

    // Update the color values
    setColors({
      primary: adjustedPrimary,
      primaryLight: primaryLight,
      secondary: secondary,
      secondaryLight: secondaryLight,
      primaryText: primaryText,
      primaryLightText: primaryLightText,
      secondaryText: secondaryText,
      secondaryLightText: secondaryLightText,
    });

    // Clear any errors
    setColorErrors({});
  };

  // Preset colors for quick selection
  const presetColors = [
    // Blues
    {
      name: "Blue",
      colors: {
        primary: "#1976d2",
        primaryLight: "#4791db",
        secondary: "#ff9800",
        secondaryLight: "#ffb74d",
        primaryText: "#ffffff",
        primaryLightText: "#ffffff",
        secondaryText: "#000000",
        secondaryLightText: "#000000",
      },
    },
    // Purples
    {
      name: "Purple",
      colors: {
        primary: "#673ab7",
        primaryLight: "#9575cd",
        secondary: "#ffc107",
        secondaryLight: "#ffe082",
        primaryText: "#ffffff",
        primaryLightText: "#ffffff",
        secondaryText: "#000000",
        secondaryLightText: "#000000",
      },
    },
    // Greens
    {
      name: "Green",
      colors: {
        primary: "#2e7d32",
        primaryLight: "#60ad5e",
        secondary: "#d32f2f",
        secondaryLight: "#e57373",
        primaryText: "#ffffff",
        primaryLightText: "#ffffff",
        secondaryText: "#ffffff",
        secondaryLightText: "#ffffff",
      },
    },
    // Reds
    {
      name: "Red",
      colors: {
        primary: "#d32f2f",
        primaryLight: "#e57373",
        secondary: "#388e3c",
        secondaryLight: "#81c784",
        primaryText: "#ffffff",
        primaryLightText: "#ffffff",
        secondaryText: "#ffffff",
        secondaryLightText: "#000000",
      },
    },
    // Orange
    {
      name: "Orange",
      colors: {
        primary: "#ff9800",
        primaryLight: "#ffb74d",
        secondary: "#2196f3",
        secondaryLight: "#64b5f6",
        primaryText: "#000000",
        primaryLightText: "#000000",
        secondaryText: "#ffffff",
        secondaryLightText: "#000000",
      },
    },
    // Teal
    {
      name: "Teal",
      colors: {
        primary: "#008080",
        primaryLight: "#20b2aa",
        secondary: "#ff6347",
        secondaryLight: "#fa8072",
        primaryText: "#ffffff",
        primaryLightText: "#ffffff",
        secondaryText: "#000000",
        secondaryLightText: "#000000",
      },
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Advanced Branding Color Editor</Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Color Input Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Edit Your Brand Colors
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(colors).map(([key, value]) => {
                // Only show the background color inputs, not text color inputs
                if (key.includes("Text")) return null;

                return (
                  <Grid item xs={6} sm={3} key={key}>
                    <TextField
                      fullWidth
                      label={key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                      value={value}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      error={Boolean(colorErrors[key])}
                      helperText={colorErrors[key] || ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "3px",
                                bgcolor: isValidHex(value) ? value : "#ccc",
                                border: "1px solid rgba(0,0,0,0.2)",
                              }}
                              onClick={() => setCurrentEditingColor(key)}
                            />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                    />
                  </Grid>
                );
              })}
            </Grid>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <Button
                variant="outlined"
                onClick={suggestColors}
                disabled={!isValidHex(colors.primary)}
              >
                Auto-suggest Harmonious Colors
              </Button>
              <Typography variant="caption" color="text.secondary">
                Click a color swatch to edit with the color picker
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Color Picker Section */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Color Picker - Editing:{" "}
                {currentEditingColor
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  p: 2,
                  bgcolor: "#f5f5f5",
                }}
              >
                <HexColorPicker
                  color={colors[currentEditingColor] || "#ffffff"}
                  onChange={handlePickerColorChange}
                  style={{ width: "100%", height: "200px" }}
                />
              </Box>

              {/* Preset Colors */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preset Color Schemes
                </Typography>
                <Grid container spacing={1}>
                  {presetColors.map((preset, index) => (
                    <Grid item key={index}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setColors(preset.colors)}
                        sx={{
                          minWidth: "auto",
                          p: 0.5,
                          border: "1px solid #ddd",
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ display: "flex" }}>
                            <Box
                              sx={{
                                width: 15,
                                height: 15,
                                bgcolor: preset.colors.primary,
                              }}
                            />
                            <Box
                              sx={{
                                width: 15,
                                height: 15,
                                bgcolor: preset.colors.primaryLight,
                              }}
                            />
                            <Box
                              sx={{
                                width: 15,
                                height: 15,
                                bgcolor: preset.colors.secondary,
                              }}
                            />
                            <Box
                              sx={{
                                width: 15,
                                height: 15,
                                bgcolor: preset.colors.secondaryLight,
                              }}
                            />
                          </Box>
                          <Typography variant="caption">
                            {preset.name}
                          </Typography>
                        </Box>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* Preview Section */}
            <Box sx={{ flex: 1 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="preview tabs"
              >
                <Tab label="Workout Plan" />
                <Tab label="UI Elements" />
              </Tabs>

              <Box
                sx={{
                  p: 2,
                  border: "1px solid #ddd",
                  borderTop: 0,
                  borderRadius: "0 0 4px 4px",
                  minHeight: "300px",
                }}
              >
                {activeTab === 0 ? (
                  // Workout Plan Preview
                  <Box>
                    {/* First Card */}
                    <Paper sx={{ mb: 2, overflow: "hidden", borderRadius: 1 }}>
                      <Box
                        sx={{
                          bgcolor: colors.primary,
                          color: colors.primaryText,
                          p: 1.5,
                          fontWeight: "bold",
                        }}
                      >
                        1: Full Body Workout
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Focus on compound movements
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.primary,
                            fontWeight: "bold",
                            mb: 0.5,
                          }}
                        >
                          Barbell Squat
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          3 sets • 8-10 reps • 60s rest
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Second Card */}
                    <Paper sx={{ overflow: "hidden", borderRadius: 1 }}>
                      <Box
                        sx={{
                          bgcolor: colors.secondary,
                          color: colors.secondaryText,
                          p: 1.5,
                          fontWeight: "bold",
                        }}
                      >
                        2: Upper Body Focus
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Target chest, back and arms
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.secondary,
                            fontWeight: "bold",
                            mb: 0.5,
                          }}
                        >
                          Dumbbell Press
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          4 sets • 10-12 reps • 45s rest
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                ) : (
                  // UI Elements Preview
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Buttons
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          bgcolor: colors.primary,
                          color: colors.primaryText,
                          "&:hover": {
                            bgcolor: colors.primaryLight,
                          },
                        }}
                      >
                        Primary
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          bgcolor: colors.secondary,
                          color: colors.secondaryText,
                          "&:hover": {
                            bgcolor: colors.secondaryLight,
                          },
                        }}
                      >
                        Secondary
                      </Button>
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>
                      Headers
                    </Typography>
                    <Paper sx={{ mb: 2, overflow: "hidden" }}>
                      <Box
                        sx={{
                          bgcolor: colors.primaryLight,
                          color: colors.primaryLightText,
                          p: 1,
                        }}
                      >
                        Primary Light Header
                      </Box>
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2">Content area</Typography>
                      </Box>
                    </Paper>

                    <Paper sx={{ overflow: "hidden" }}>
                      <Box
                        sx={{
                          bgcolor: colors.secondaryLight,
                          color: colors.secondaryLightText,
                          p: 1,
                        }}
                      >
                        Secondary Light Header
                      </Box>
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2">Content area</Typography>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={Object.values(colorErrors).some(Boolean)}
        >
          Apply Colors
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedBrandingEditorDialog;
